import { Card } from "components";
import { CardSection } from "../CardSection";
import { BalanceSheet } from "../GrafikSection";
import { PiWarningCircleFill } from "react-icons/pi";
import { Expenses } from "./Expenses";
import { useAveragePaySupplier, useAverageTime, useFinancialPosition, useOveralCashBalance } from "../../utils";
import { convertNumber } from "utils";
import moment from "moment";

export function FinancialPosition(params: {
    month?: string | undefined,
    year?: string | undefined,
    location_id?: string
}) {

    const { data: averageTime } = useAverageTime({
        date: `${params.year}-${params.month}-01`,
        location_id: params.location_id ? params.location_id : ""
    })

    const { data: averagePaySupplier } = useAveragePaySupplier({
        date: `${params.year}-${params.month}-01`,
        location_id: params.location_id ? params.location_id : ""
    })

    


    return (
        <section>
            <div>
                <span className="font-bold text-[15px] mr-2">Financial position and cash</span>
            </div>
            <div className="grid lg:grid-cols-3 mt-2 gap-2 h-full">
                <BalanceSheetSection
                    location_id={params.location_id!}
                    month={params.month}
                    year={params.year}
                />

                <OveralCashBalance
                    location_id={params.location_id!}
                    month={params.month}
                    year={params.year}
                />

                <div className="h-full">
                    <div className="grid grid-rows-2 gap-2 h-full">
                        <AverageTime
                            value={Number(averageTime?.dso)!}
                            subValue={Number(averageTime?.total_credit_sales!)}
                            month={params.month}
                            year={params.year}
                            label="Average time to get paid"
                            subLabel="Value of unpaid invoices"
                            subLabel2="Value of all invoices"
                            subvalue2={Number(averageTime?.value_of_all_invoice ?? 0)}
                            numberOfdays={Number(averageTime?.number_of_days)}
                        />
                        <AverageTime
                            value={Number(averagePaySupplier?.dpo)!}
                            subValue={Number(averagePaySupplier?.unpaid_bill )?? 0}
                            month={params.month}
                            year={params.year}
                            label="Average time to pay supplier"
                            subLabel="Value of unpaid bills"
                            subLabel2="Value of all bills"
                            subvalue2={Number(averagePaySupplier?.value_of_all_bill ?? 0)}
                            numberOfdays={Number(averagePaySupplier?.number_of_days)}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-4 overflow-x-auto">
                <Expenses
                    location_id={params.location_id!}
                    month={params.month}
                    year={params.year}
                />
            </div>
        </section>
    )
}

function BalanceSheetSection(params: {
    month: string | undefined,
    year: string | undefined,
    location_id: string
}) {

    const { data } = useFinancialPosition({
        date: `${params.year}-${params.month}-01`,
        location_id: params.location_id ? params.location_id : ""
    })

    return (
        <div className="h-full">
            <Card className="h-full">
                <Card.Body className="h-full flex flex-col justify-between">
                    <CardSection label="Balance sheet">
                        <div className="mt-[20px]">
                            <div className="border-b-2 pb-2 mb-2">
                                Assets: <b>{data?.assets ? convertNumber(data?.assets).intoCurrency : "0"}</b>
                            </div>
                            <div className="mb-2">
                                Liabilities: <b>{data?.liabilities ? convertNumber(data?.liabilities).intoCurrency : "0"}</b>
                            </div>
                            <div>
                                Equity: <b>{data?.equities ? convertNumber(data?.equities).intoCurrency : "0"}</b>
                            </div>
                        </div>
                        <div className="mt-[50px]">
                            <BalanceSheet
                                assets={data?.assets_percentage!}
                                liabilities={data?.liabilities_percentage!}
                                equities={data?.equities_percentage!}
                            />
                        </div>
                        <div className="mt-5">
                            Assets are equal to liabilities plus equity
                        </div>
                    </CardSection>
                </Card.Body>
            </Card>
        </div>
    )
}

function OveralCashBalance(params: {
    month: string | undefined,
    year: string | undefined,
    location_id: string
}) {

    const { data } = useOveralCashBalance({
        date: `${params.year}-${params.month}-01`,
        location_id: params.location_id ? params.location_id : ""
    })

    return (
        <div className="h-full">
            <Card className="h-full">
                <Card.Body className="h-full flex flex-col flex-grow justify-between">
                    <CardSection
                        label="Overall cash balance"
                        value={data?.overall_cash_balance ? convertNumber(data?.overall_cash_balance).intoCurrency : "0"}
                        icon={<PiWarningCircleFill />}
                    >
                        <div className="mt-[10px] overflow-y-auto max-h-[300px]">
                            {data?.coa_cash_balance.map((item, index) => (
                                <div className="mb-2" key={index}>
                                    <div>{item.coa_name}</div>
                                    <div><b>{item.cash_balance ? convertNumber(item.cash_balance).intoCurrency : "0"}</b></div>
                                </div>
                            ))}
                        </div>
                    </CardSection>
                </Card.Body>
            </Card>
        </div>

    )
}

function AverageTime(params: {
    value: number,
    subValue: number,
    month: string | undefined,
    year: string | undefined,
    label: string,
    subLabel: string
    numberOfdays?: number
    subLabel2: string
    subvalue2: number
}) {
    const startMonthNew = moment(`${params.year}-${params.month}`).startOf("month").format("D MMMM YYYY");
    const endOfMonth = moment(`${params.year}-${params.month}`).endOf("month").format("D MMMM YYYY");

    return (
        <Card className="h-full">
            <Card.Body className="h-full flex flex-col justify-between">
                <CardSection
                    label={params.label}
                    value={`${params.value ? convertNumber(Number(params.value.toFixed(2))).intoNormalAmount : 0} days`}
                    icon={<PiWarningCircleFill />}
                    subLabel={`${params.numberOfdays} days from ${startMonthNew} - ${endOfMonth}`}
                >
                    <div className="mb-2">
                        {params.subLabel}: <b>{params.subValue ? convertNumber(params.subValue).intoCurrency : 0}</b>
                    </div>
                    <div>
                        {params.subLabel2}: <b>{params.subLabel2 ? convertNumber(params.subvalue2).intoCurrency : 0}</b>
                    </div>
                </CardSection>
            </Card.Body>
        </Card>
    )
}